import { useState } from "react";
import { FormLabel, Radio, RadioGroup } from "@mui/material";
import SubmissionModal from "../../../components/SubmissionModal";
import {
  NewArtistReviewStatus,
  NewArtistStatus,
  NewArtistsContactSubmission,
  getNewArtistStatusMeta,
} from "../../../api/new-artists";
import useReviewNewArtistMutation, {
  getReviewErrorMessage,
} from "../hooks/useReviewNewArtistMutation";
import useUsers from "components/hooks/useUsers";
import { resolveUserName } from "../../Tickets/utils";
import ConfirmationModal from "../../ConfirmationModal";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSChip from "../../system-design/DSChip";
import DSFormControl from "../../system-design/DSFormControl";
import DSFormControlLabel from "../../system-design/DSFormControlLabel";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSAlert from "../../system-design/DSAlert";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSTextField from "../../system-design/DSTextField";

const ACTION_OPTIONS: { value: NewArtistReviewStatus; label: string }[] = [
  { value: NewArtistStatus.Approved, label: "Approve" },
  { value: NewArtistStatus.Rejected, label: "Reject" },
  { value: NewArtistStatus.Clarification, label: "Request clarification" },
];

const NewArtistReviewModal = (props: {
  row?: NewArtistsContactSubmission;
  setShowModal: (show: boolean) => void;
}) => {
  const row = props.row;

  const [action, setAction] = useState<NewArtistReviewStatus | "">("");
  const [sendEmail, setSendEmail] = useState<"yes" | "no" | "">("");
  const [notes, setNotes] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: users } = useUsers();

  const {
    mutate: reviewSubmission,
    isPending,
    isError,
    error,
  } = useReviewNewArtistMutation({
    onSuccess: () => props.setShowModal(false),
  });

  if (!row) return null;

  const current = row.reviewDetails?.status ?? NewArtistStatus.Pending;
  const currentMeta = getNewArtistStatusMeta(current);
  // A review is terminal once an email has been sent to the artist — the
  // controls are removed (we never notify the same artist twice).
  const emailAlreadySent = row.reviewDetails?.emailSentAt != null;
  const actionLabel = action
    ? ACTION_OPTIONS.find((option) => option.value === action)?.label
    : "";

  const canSubmit = action !== "" && sendEmail !== "" && !isPending;

  const confirmMessage =
    action && sendEmail
      ? `Are you sure you want to ${
          sendEmail === "yes" ? "send the" : "NOT send a"
        } "${actionLabel}" email to ${row.fullName || "this artist"} (${
          row.email
        })?`
      : "";

  const handleConfirm = () => {
    if (action === "" || sendEmail === "") return;
    reviewSubmission({
      id: row.id,
      status: action,
      sendEmail: sendEmail === "yes",
      notes: notes.trim() || null,
    });
  };

  return (
    <>
      <SubmissionModal row={row} setShowModal={props.setShowModal} />

      <DSBox
        sx={{
          px: 3,
          pb: 3,
          pt: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <DSBox sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <DSTypography variant="h6" sx={{ m: 0 }}>
            Review
          </DSTypography>
          <DSChip
            label={currentMeta.label}
            color={currentMeta.color}
            size="small"
            variant="outlined"
          />
        </DSBox>

        {row.reviewDetails?.reviewedAt && (
          <DSTypography sx={{ fontSize: 13, color: "text.secondary" }}>
            Last reviewed{" "}
            {new Date(row.reviewDetails.reviewedAt).toLocaleString()}
            {row.reviewDetails.reviewedBy
              ? ` by ${resolveUserName(row.reviewDetails.reviewedBy, users ?? [])}`
              : ""}
            {row.reviewDetails.emailSentAt
              ? ` · email sent ${new Date(
                  row.reviewDetails.emailSentAt,
                ).toLocaleString()}`
              : " · no email sent"}
          </DSTypography>
        )}

        {row.reviewDetails?.notes && (
          <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <DSTypography sx={{ fontSize: 13, fontWeight: 700 }}>
              Review notes
            </DSTypography>
            <DSBox
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "paper.elevation4",
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {row.reviewDetails.notes}
            </DSBox>
          </DSBox>
        )}

        {emailAlreadySent ? (
          <DSAlert severity="info">
            A review email has already been sent to this artist — no further
            review actions are available.
          </DSAlert>
        ) : (
          <>
            <DSFormControl required>
              <FormLabel sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Review action
              </FormLabel>
              <RadioGroup
                value={action}
                onChange={(e) =>
                  setAction(e.target.value as NewArtistReviewStatus)
                }
              >
                {ACTION_OPTIONS.map((option) => (
                  <DSFormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
            </DSFormControl>

            <DSFormControl required>
              <FormLabel sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Send email to artist
              </FormLabel>
              <RadioGroup
                row
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value as "yes" | "no")}
              >
                <DSFormControlLabel
                  value="yes"
                  control={<Radio size="small" />}
                  label="Yes"
                />
                <DSFormControlLabel
                  value="no"
                  control={<Radio size="small" />}
                  label="No"
                />
              </RadioGroup>
            </DSFormControl>

            <DSFormControl>
              <FormLabel sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Notes (optional)
              </FormLabel>
              <DSTextField
                multiline
                minRows={3}
                fullWidth
                placeholder="Internal notes about this submission"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </DSFormControl>

            {isError && (
              <DSAlert severity="error">{getReviewErrorMessage(error)}</DSAlert>
            )}

            <DSBox sx={{ display: "flex", justifyContent: "flex-end" }}>
              <DSPrimaryButton
                disabled={!canSubmit}
                onClick={() => setConfirmOpen(true)}
              >
                {isPending ? (
                  <DSCircularProgress size={20} color="inherit" />
                ) : (
                  "Submit review"
                )}
              </DSPrimaryButton>
            </DSBox>
          </>
        )}
      </DSBox>

      {!emailAlreadySent && (
        <ConfirmationModal
          open={confirmOpen}
          message={confirmMessage}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            handleConfirm();
          }}
        />
      )}
    </>
  );
};

export default NewArtistReviewModal;
